import { OverlayTrigger, ProgressBar, Tooltip } from "react-bootstrap";

import { QuantityWithUOMAbbr, UOMLabelInfo } from "./QuantityWithUOMAbbr";
import { ItemUOMSelectResult } from "@/features/job-tracking/queries/tracking-data/item-uoms";
import { Icon } from "./Icon";

export interface CountingProgressBarProps {
  now: number;
  max: number;
  selectedItemUOM?: ItemUOMSelectResult | null;
  baseUOM?: UOMLabelInfo | null;
}

/**
 * A progress par that shows progression towards a total value. The label is centered on the bar, and shows the current
 * value and the total value. The light theme is forced on the element to ensure the text is readable.
 */
export const CountingProgressBar: React.FC<CountingProgressBarProps> = ({ now, max, selectedItemUOM, baseUOM }) => {
  // Select a color variant based on the current value (`now`) compared to the maximum value (`max`).
  const variant =
    now === max ? "success"
    : now > max ? "danger"
    : "warning";

  // Calculate what percentage of the bar should be filled; customize logic so that x/0 is always 100%
  const barPercentage = max ? now / max : 1;

  // In some cases, we may need to show the variant as a warning icon because the fill color of the progress bar is not
  // visible enough (i.e. if the now value is 0 there is no colored bar)
  const variantIcon =
    variant === "success" || barPercentage > 0.1 ?
      null
    : <span className={`ms-1 text-${variant}`}>
        <Icon icon="exclamation-triangle-fill" />
      </span>;

  // Define the default label, which displays the quantity with the selected UOM
  const label = (
    <>
      <QuantityWithUOMAbbr quantity={now} /> / <QuantityWithUOMAbbr quantity={max} uom={selectedItemUOM?.uom} />
      {variantIcon}
    </>
  );

  // Optionally wrap the label with a tooltip that shows the base UOM quantity. This is only added if the selected UOM is not the base UOM.
  const labelWithBaseUOMTooltip =
    !selectedItemUOM || selectedItemUOM.isBase || !baseUOM ?
      <span className="progress-bar-label-centered">{label}</span>
    : <OverlayTrigger
        overlay={
          <Tooltip>
            <QuantityWithUOMAbbr quantity={now} /> /
            <QuantityWithUOMAbbr quantity={max} uom={baseUOM} />
          </Tooltip>
        }
      >
        <a className="progress-bar-label-centered">{label}</a>
      </OverlayTrigger>;

  // If progress is x/0, we need to modify the values passed to the ProgressBar component
  // so that it appears full instead of empty
  const barNow = !max && !now ? 1 : now;
  const barMax = !max ? barNow : max;

  return (
    <ProgressBar data-bs-theme="light" now={barNow} max={barMax} label={labelWithBaseUOMTooltip} variant={variant} />
  );
};
